import { useCreateEvent } from "@/features/events/hooks/useCreateEvent";
import { EventType } from "@/features/events/types/EventType";
import { useEffect, useState } from "react";
import { ClickableItem, HealthPlanData } from "../../../utils/api-objects/HealthPlan";
import { callUpdateHealthPlanDataApi } from "../../../utils/apis/plan-service";
import { saveEvent } from "../../../utils/apis/user-service";
import { useAuthStore } from "../../../utils/stores/useAuthStore";
import { usePlansStore } from "../../../utils/stores/usePlansStore";
import { useUserStore } from "../../../utils/stores/useUserStore";

export const HealthPlanDataBox: React.FC<{
    title: string,
    data: string[],
    source: string,
}> = ({
    title,
    data,
    source,
}) => {
        const { sendEvent } = useCreateEvent()
        const { userData } = useUserStore()
        const email = userData.email
        const [isExpanded, setIsExpanded] = useState(false)

        const handleTitle = () => {
            if (title.includes("Commento")) {
                return "comment";
            }
            return "";
        };

        const onShowMoreClick = async () => {
            setIsExpanded(!isExpanded)

            if (!email) return
            saveEvent(
                email,
                isExpanded ? `show_less_${handleTitle()}_button_clicked` : `show_more_${handleTitle()}_button_clicked`,
                (success) => { console.log() },
                (error) => { console.log(error) },
                source,
            )

            await sendEvent({
                event: EventType.CLICK,
                sub_category: "health_plan_interaction",
                event_metadata: { action: "show_more/less_on_card", title }
            })
        }

        // ********************************************************************************

        return (
            <div className={`self-start w-full border border-solid border-border rounded-lg shadow-platform-new px-[20px] py-[10px] flex flex-col space-y-[10px] bg-white`}>
                <div
                    className="font-bold text-[12px] leading-[33px]"
                >
                    {title}
                </div>
                <div
                    className={`relative overflow-hidden transition-all ${isExpanded ? "h-auto md:min-h-[70px] space-y-[10px]" : "max-h-[70px] md:h-[70px]"}`}
                >
                    {data.map((element, index) => (
                        <div key={index}>
                            <span
                                className={`font-normal text-[16px] leading-[24px] text-black`}
                            >
                                {isExpanded ? "• " : ""}{element}
                            </span>
                        </div>
                    ))}

                    {!isExpanded && (
                        <div className="absolute bottom-0 left-0 w-full h-[100px] bg-gradient-to-t from-white to-transparent pointer-events-none" />
                    )}
                </div>

                <div className="w-full">
                    <div
                        onClick={onShowMoreClick}
                        className="text-holifya-blue font-bold text-[12px]"
                    >
                        {isExpanded ? "Mostra meno" : "Mostra di più"}
                    </div>
                </div>
            </div>
        );
    }


export const HealthPlanDataBoxWithCheckbox: React.FC<{
    title: string,
    data: ClickableItem[],
    fullData: HealthPlanData,
    planId: string,
    source: string,
}> = ({
    title,
    data,
    fullData,
    planId,
    source,
}) => {
        const { sendEvent } = useCreateEvent()
        const { auth } = useAuthStore()
        const idToken = auth.idToken
        // const { userData } = useUserData()
        // const email = userData.email
        const { userData } = useUserStore()
        const email = userData.email
        const { plans, updatePlans } = usePlansStore()
        const healthPlans = plans.healthPlans
        const [isExpanded, setIsExpanded] = useState(false)
        const [checkedItems, setCheckedItems] = useState<boolean[]>(data.map(item => item.isChecked ?? false));

        const [progress, setProgress] = useState(0);

        useEffect(() => {
            const checkedCount = checkedItems.filter(item => item).length;
            const totalCount = checkedItems.length;
            setProgress(totalCount > 0 ? (checkedCount / totalCount) * 100 : 0);
        }, [checkedItems]);

        const handleTitle = () => {
            if (title.includes("Alimenti")) {
                return "food_suggested";
            } else if (title.includes("Integratori")) {
                return "integrators_suggested"
            } else if (title.includes("Check")) {
                return "check_ups_suggested"
            }
            return "";
        };

        const onShowMoreButtonClicked = async () => {
            setIsExpanded(!isExpanded)
            if (!email) return
            saveEvent(
                email,
                isExpanded ? `show_less_${handleTitle()}_button_clicked` : `show_more_${handleTitle()}_button_clicked`,
                (success) => console.log(success),
                (error) => console.log(error),
                source,
            )

            await sendEvent({
                event: EventType.CLICK,
                sub_category: "health_plan_interaction",
                event_metadata: { action: "show_more/less_on_card", title }
            })
        }

        const updateCheckboxStatus = async (index: number) => {
            try {
                const isChecked = !checkedItems[index];

                // Update the local state immediately
                const updatedCheckedItems = [...checkedItems];
                updatedCheckedItems[index] = isChecked;
                setCheckedItems(updatedCheckedItems);

                // Update the data array with the modified item
                const updatedData = data.map((item, i) =>
                    i === index ? { ...item, isChecked } : item // Update the changed item, keep the others as is
                );

                let key = ""
                switch (title) {
                    case "🥙  Alimenti consigliati":
                        key = "food_suggested"
                        break
                    case "💊  Integratori consigliati":
                        key = "integrators_suggested"
                        break
                    case "🩺  Check-up consigliati":
                        key = "check_ups_suggested"
                        break
                    default:
                        console.error("Invalid title");
                        return;
                }

                // Update only the specific key in fullData
                const updatedFullData = {
                    ...fullData,
                    [key]: updatedData, // Replace only the modified field
                };

                console.log("Updated Full Data:", updatedFullData);

                // Call the API to update the health plan
                const response = await callUpdateHealthPlanDataApi(idToken!, planId, updatedFullData);

                const result = await response.json();
                console.log("Updated plan:", result.updated_plan);
                console.log("HPs before update:", healthPlans);

                if (result.updated_plan && healthPlans) {
                    // Replace the updated health plan in the list
                    const updatedHealthPlans = healthPlans.map((hp) =>
                        hp.id === result.updated_plan.id ? result.updated_plan : hp
                    );

                    // Update state with the new health plans
                    updatePlans({ healthPlans: updatedHealthPlans });
                    console.log("HPs after update:", updatedHealthPlans);

                    if (!email) return
                    saveEvent(
                        email,
                        `${isChecked ? "toggle" : "untoggle"}_on_${key}_${data[index].text}`,
                        (success) => { console.log() },
                        (error) => { console.log(error) },
                        source,
                    )
                    await sendEvent({
                        event: EventType.CLICK,
                        sub_category: "health_plan_interaction",
                        event_metadata: { action: "toggle/untoggle_on_keys", key, value: data[index].text }
                    })
                } else {
                    console.error("Failed to get updated plan from API");
                }

                if (!result.success) {
                    console.error("Failed to update checkbox status");

                    // Optionally revert the change if the update fails
                    setCheckedItems(prev => {
                        const newCheckedItems = [...prev];
                        newCheckedItems[index] = !newCheckedItems[index]; // Revert the change
                        return newCheckedItems;
                    });
                }
            } catch (error) {
                console.error("Error updating checkbox status:", error);
            }
        };

        const handleCheckboxChange = (index: number) => {
            // const a  = !data[index].isChecked
            // console.log(a)
            // console.log(data)
            // setCheckedItems((prev) => {
            //     const newCheckedItems = [...prev];
            //     newCheckedItems[index] = !newCheckedItems[index];
            //     return newCheckedItems;
            // });

            updateCheckboxStatus(index);
        };

        const handleSecondText = () => {
            switch (title) {
                case "🥙  Alimenti consigliati":
                    return "Fanno parte della tua alimentazione quotidiana?"
                case "💊  Integratori consigliati":
                    return "Li hai già inseriti nella tua integrazione?"
                case "🩺  Check-up consigliati":
                    return "Hai già in programma di farli?"
                default:
                    return ""
            }
        }

        // ********************************************************************************

        return (
            <div className={`self-start w-full border border-solid border-border rounded-lg shadow-platform-new px-[20px] py-[10px] flex flex-col space-y-[10px] bg-white`}>
                <div>
                    <div className="flex items-center justify-between">
                        <div
                            className="font-bold text-[12px] leading-[33px]"
                        >
                            {title}
                        </div>
                        <div className="relative w-[20px] h-[20px] flex items-center justify-center">
                            {/* Background Ring (Grey) */}
                            <svg className="absolute top-0 left-0 w-full h-full" viewBox="0 0 36 36">
                                <circle
                                    cx="18"  // Adjusted center point
                                    cy="18"
                                    r="16"  // Keeps 18px radius
                                    stroke="#F2F2F2"
                                    strokeWidth="4"  // Keeps 4px stroke width
                                    fill="none"
                                />
                            </svg>

                            {/* Progress Ring */}
                            <svg className="absolute top-0 left-0 w-full h-full rotate-[-90deg]" viewBox="0 0 36 36">
                                <circle
                                    cx="18"  // Adjusted center point
                                    cy="18"
                                    r="16"
                                    stroke="#3BB123"
                                    strokeWidth="4"
                                    strokeDasharray="100, 100"
                                    strokeDashoffset={`${100 - progress}`}
                                    strokeLinecap="round"
                                    fill={progress === 100 ? "#9AED89" : "none"}
                                    style={{ transition: "stroke-dashoffset 0.5s ease-in-out" }}
                                />
                            </svg>

                            {progress === 100 && (
                                <span className="absolute w-[8px] text-[8px] leading-none">
                                    🎉
                                </span>
                            )}
                        </div>
                    </div>
                    {
                        isExpanded && <div className="font-normal text-[12px] leading-[30px]">
                            {handleSecondText()}
                        </div>
                    }
                </div>


                <div
                    className={`relative overflow-hidden transition-all ${isExpanded ? "h-auto md:min-h-[40px] space-y-[10px]" : "max-h-[70px] md:h-[70px]"}`}
                >
                    {data.map((item, index) => (
                        <div key={index} className="flex items-center space-x-2">
                            {isExpanded && (
                                <label className="relative flex items-center">
                                    <input
                                        type="checkbox"
                                        checked={checkedItems[index]}
                                        onChange={() => handleCheckboxChange(index)}
                                        className="w-[18px] h-[18px] cursor-pointer 
                                           appearance-none border-2 border-black 
                                           rounded-[4px] flex items-center justify-center 
                                           checked:bg-[#3BB123] checked:border-[#3BB123] 
                                           relative"
                                    />
                                    {/* White checkmark when checked */}
                                    {checkedItems[index] && (
                                        <svg
                                            className="absolute left-[3px] top-[3px] w-[12px] h-[12px] text-white"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                            strokeWidth={4}
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                        </svg>
                                    )}
                                </label>
                            )}
                            <span
                                className={`text-[16px] leading-[24px] ${checkedItems[index] ? "text-[#3BB123] font-bold" : "text-black font-normal"}`}
                            >
                                {item.text}
                            </span>
                        </div>
                    ))}

                    {!isExpanded && (
                        <div className="absolute bottom-0 left-0 w-full h-[100px] bg-gradient-to-t from-white to-transparent pointer-events-none" />
                    )}
                </div>

                <div className="w-full">
                    <div
                        onClick={onShowMoreButtonClicked}
                        className="text-holifya-blue font-bold text-[12px]"
                    >
                        {isExpanded ? "Mostra meno" : "Mostra di più"}
                    </div>
                </div>
            </div>
        );
    }